import { FormInstance } from 'antd';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFullAddress } from '@/hooks';
import { INormalizedAddress } from '@/types';

const ADDRESS_FIELDS = [
  'province',
  'city',
  'residenceLocality',
  'streetType',
  'streetNumber',
  'postalCode',
  'address'
] as const;

const NORMALIZED_ADDRESS_KEYS: (keyof INormalizedAddress)[] = [
  'address',
  'province',
  'city',
  'residenceLocality',
  'streetType',
  'streetNumber',
  'postalCode',
  'countryResidence'
];

const EXTRA_ADDRESS_KEYS = ['staircase', 'floor', 'door'] as const;

const extractAddressFields = (
  source: Partial<INormalizedAddress> | null | undefined
): INormalizedAddress | null => {
  if (!source) return null;
  return NORMALIZED_ADDRESS_KEYS.reduce((acc, key) => {
    acc[key] = source[key];
    return acc;
  }, {} as Record<keyof INormalizedAddress, string | undefined>) as INormalizedAddress;
};

const createEmptyAddressFields = (): Record<string, null> => {
  const allKeys = [...NORMALIZED_ADDRESS_KEYS, ...EXTRA_ADDRESS_KEYS];
  return allKeys.reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {} as Record<string, null>);
};

type AddressField = (typeof ADDRESS_FIELDS)[number];

interface UseAddressFieldsParams {
  form: FormInstance;
  wrapperRef: RefObject<HTMLDivElement>;
}

export const useAddressFields = ({ form, wrapperRef }: UseAddressFieldsParams) => {
  const [autocompleteError, setAutocompleteError] = useState<string | null>(null);

  const [selectedAddress, setSelectedAddress] = useState<INormalizedAddress | null>(() => {
    const initialUserInfo = form.getFieldValue(['userInfo']);
    if (initialUserInfo?.address) {
      return extractAddressFields(initialUserInfo);
    }
    return null;
  });

  const fullAddress = useFullAddress(selectedAddress || undefined);

  const [initialAddressForAutocomplete] = useState<string | undefined>(() => {
    const initialUserInfo = form.getFieldValue(['userInfo']);
    if (initialUserInfo?.address) {
      return `${initialUserInfo.streetType || ''} ${initialUserInfo.address || ''}, ${
        initialUserInfo.city || ''
      }, ${initialUserInfo.province || ''}`.trim();
    }
    return undefined;
  });

  const hasHydratedRef = useRef(false);
  const userInfo = form.getFieldValue(['userInfo']);

  useEffect(() => {
    if (hasHydratedRef.current) return;
    if (!userInfo?.address) return;
    if (selectedAddress?.address) {
      hasHydratedRef.current = true;
      return;
    }

    const hydratedAddress = extractAddressFields(userInfo);
    if (hydratedAddress) {
      setSelectedAddress(hydratedAddress);
    }
    hasHydratedRef.current = true;
  }, [userInfo?.address]);

  const handleAddressSelect = useCallback(
    (address: INormalizedAddress) => {
      setSelectedAddress(address);
      form.setFieldsValue({
        userInfo: extractAddressFields(address)
      });
    },
    [form]
  );

  const handleAddressClear = useCallback(() => {
    setSelectedAddress(null);
    form.setFieldsValue({
      userInfo: createEmptyAddressFields()
    });
  }, [form]);

  const missingFields = useMemo((): AddressField[] => {
    if (!selectedAddress?.address) return [];
    return ADDRESS_FIELDS.filter(field => !selectedAddress[field]);
  }, [selectedAddress]);

  const hasSelectedAddress = !!selectedAddress?.address;
  const hasMissingFields = missingFields.length > 0;
  const shouldShowAllFields = !!autocompleteError;

  const shouldShowField = useCallback(
    (field: AddressField): boolean => {
      if (shouldShowAllFields) return true;
      return hasSelectedAddress && missingFields.includes(field);
    },
    [hasSelectedAddress, missingFields, shouldShowAllFields]
  );

  useEffect(() => {
    if (hasMissingFields && hasSelectedAddress) {
      setTimeout(() => {
        if (wrapperRef.current) {
          let scrollableParent: HTMLElement | null = wrapperRef.current.parentElement;

          while (scrollableParent) {
            const style = window.getComputedStyle(scrollableParent);
            const hasOverflowY = style.overflowY === 'auto' || style.overflowY === 'scroll';
            const hasHeight = scrollableParent.scrollHeight > scrollableParent.clientHeight;

            if (hasOverflowY && hasHeight) {
              const elementTop = wrapperRef.current.offsetTop;
              const elementHeight = wrapperRef.current.offsetHeight;
              const parentHeight = scrollableParent.clientHeight;

              scrollableParent.scrollTo({
                top: elementTop + elementHeight - parentHeight + 20,
                behavior: 'smooth'
              });
              return;
            }

            scrollableParent = scrollableParent.parentElement;
          }

          const modalBody = document.querySelector('.ant-modal-body') as HTMLElement;
          if (modalBody && wrapperRef.current) {
            const elementTop = wrapperRef.current.getBoundingClientRect().top;
            const modalBodyTop = modalBody.getBoundingClientRect().top;
            const relativeTop = elementTop - modalBodyTop + modalBody.scrollTop;
            const elementHeight = wrapperRef.current.offsetHeight;
            const parentHeight = modalBody.clientHeight;

            modalBody.scrollTo({
              top: relativeTop + elementHeight - parentHeight + 20,
              behavior: 'smooth'
            });
          }
        }
      }, 150);
    }
  }, [hasMissingFields, hasSelectedAddress, selectedAddress?.address, wrapperRef]);

  return {
    selectedAddress,
    fullAddress,
    initialAddressForAutocomplete,
    autocompleteError,
    setAutocompleteError,
    handleAddressSelect,
    handleAddressClear,
    hasSelectedAddress,
    hasMissingFields,
    shouldShowAllFields,
    shouldShowField
  };
};
